import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Bell, HelpCircle } from 'lucide-react';
import { keycloakLogout } from '@favelafood/shared/auth/keycloak';
import { MerchantSidebar } from '@favelafood/shared/components/merchant/shell/MerchantSidebar';
import { usePendingCount } from '@/api';
import { STAFF_NAV } from './nav';

/**
 * Root shell for the staff back-office app. Mirrors the old `MerchantShell`
 * staff branch, but self-contained (no owner memberships / StoreSwitcher /
 * merchant variant): sidebar + a minimal onboarding topbar + the routed outlet.
 *
 * Auth is OIDC-only here (no password grant): `onLogout` goes straight through
 * `keycloakLogout()` (default post-logout = `${origin}/`, which re-drives the
 * PanelGuard). It deliberately does NOT route through `useAuth.logout`, whose
 * area-aware `/admin/login` target is wrong for this subdomain.
 */
export function StaffShell(): JSX.Element {
  const pendingCount = usePendingCount();

  // Inject the live pending count as the "Pendentes" badge.
  const nav = useMemo(() => {
    if (pendingCount <= 0) return STAFF_NAV;
    return STAFF_NAV.map((section) => ({
      ...section,
      items: section.items.map((item) =>
        item.to === '/pendentes' ? { ...item, badge: pendingCount } : item
      ),
    }));
  }, [pendingCount]);

  return (
    <div className="merchant-theme flex min-h-screen bg-[var(--m-surface-muted)] text-[var(--m-fg)]">
      <MerchantSidebar
        badge="ONBOARDING"
        nav={nav}
        user={{ name: 'Minha conta', role: 'Equipe · Onboarding' }}
        onLogout={() => keycloakLogout()}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-[var(--m-divider)] bg-[var(--m-surface)] px-6 py-3">
          <div className="text-sm font-medium text-[var(--m-fg)]">Onboarding</div>
          <div className="flex items-center gap-2">
            <button
              aria-label="Notificações"
              className="rounded-[var(--m-r-md)] p-2 text-[var(--m-fg-muted)] hover:bg-[var(--m-surface-muted)]"
            >
              <Bell size={18} />
            </button>
            <button
              aria-label="Ajuda"
              className="rounded-[var(--m-r-md)] p-2 text-[var(--m-fg-muted)] hover:bg-[var(--m-surface-muted)]"
            >
              <HelpCircle size={18} />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
